import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { 
  Plus, Trash2, Search, MapPin, Edit, Phone, User
} from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export const CityAdminsManagement = () => {
  const { user } = useAuth();
  const [cityAdmins, setCityAdmins] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editAdmin, setEditAdmin] = useState(null);
  const [deleteAdmin, setDeleteAdmin] = useState(null);
  const [newAdmin, setNewAdmin] = useState({ name: "", phone: "", city_id: "" });

  useEffect(() => { 
    if (user?.id) {
      fetchData(); 
    }
  }, [user?.id]);

  const fetchData = async () => {
    try {
      const [adminsRes, citiesRes] = await Promise.all([
        axios.get(`${API}/admin/city-admins?admin_id=${user.id}`),
        axios.get(`${API}/cities?admin_id=${user.id}`)
      ]);
      setCityAdmins(adminsRes.data);
      // Sort cities alphabetically
      const sortedCities = (citiesRes.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCities(sortedCities);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newAdmin.name || !newAdmin.phone || !newAdmin.city_id) {
      toast.error("Please fill all required fields");
      return;
    }
    if (newAdmin.phone.length !== 10) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    try {
      await axios.post(`${API}/admin/city-admins?admin_id=${user.id}`, newAdmin);
      toast.success("City Admin created successfully");
      setShowAddDialog(false);
      setNewAdmin({ name: "", phone: "", city_id: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create city admin");
    }
  };

  const handleUpdate = async () => {
    if (!editAdmin) return;
    try {
      await axios.put(`${API}/admin/city-admins/${editAdmin.id}?admin_id=${user.id}`, {
        name: editAdmin.name,
        phone: editAdmin.phone,
        email: editAdmin.email,
        assigned_city_id: editAdmin.assigned_city_id,
        is_active: editAdmin.is_active,
      });
      toast.success("City Admin updated");
      setEditAdmin(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const handleDelete = async () => {
    if (!deleteAdmin) return;
    try {
      await axios.delete(`${API}/admin/city-admins/${deleteAdmin.id}?admin_id=${user.id}`);
      toast.success("City Admin deleted");
      setDeleteAdmin(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const getCityName = (cityId) => {
    const city = cities.find(c => c.id === cityId);
    return city?.name || "Unknown City";
  };

  const filteredAdmins = cityAdmins.filter(admin =>
    admin.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    admin.phone?.includes(searchTerm) ||
    admin.assigned_city_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="city-admins-management">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">City Admins</h2>
          <p className="text-muted-foreground">
            {cityAdmins.length} city admin{cityAdmins.length !== 1 ? 's' : ''} managing {cities.length} {cities.length !== 1 ? 'cities' : 'city'}
          </p>
        </div>
        <Button 
          onClick={() => setShowAddDialog(true)} 
          disabled={cities.length === 0}
          data-testid="add-city-admin-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add City Admin
        </Button>
      </div>

      {cities.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-yellow-800 font-medium">No cities available</p>
          <p className="text-sm text-yellow-600">Create a city first before adding city admins.</p>
        </div>
      )}

      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input 
          placeholder="Search by name, phone, or city..." 
          value={searchTerm} 
          onChange={(e) => setSearchTerm(e.target.value)} 
          className="pl-10" 
        />
      </div>

      {/* Mobile Cards View */}
      <div className="md:hidden space-y-3">
        {filteredAdmins.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground bg-card rounded-xl border border-border">
            <MapPin className="w-12 h-12 mx-auto mb-2 text-muted-foreground/50" />
            <p>No city admins yet</p>
          </div>
        ) : (
          filteredAdmins.map((admin) => (
            <div key={admin.id} className="bg-card rounded-xl border border-border p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">{admin.name}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {admin.phone}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => setEditAdmin({...admin})}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setDeleteAdmin(admin)} className="text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                  {admin.assigned_city_name || getCityName(admin.assigned_city_id)}
                </span>
                <span className={`text-xs ${admin.is_active !== false ? 'text-green-600' : 'text-red-600'}`}>
                  {admin.is_active !== false ? 'Active' : 'Disabled'}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-card rounded-xl border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>City</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAdmins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No city admins found
                </TableCell>
              </TableRow>
            ) : (
              filteredAdmins.map((admin) => (
                <TableRow key={admin.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="font-medium">{admin.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>{admin.phone}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-sm">
                      <MapPin className="w-3 h-3" />
                      {admin.assigned_city_name || getCityName(admin.assigned_city_id)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`text-sm ${admin.is_active !== false ? 'text-green-600' : 'text-red-600'}`}>
                      {admin.is_active !== false ? 'Active' : 'Disabled'}
                    </span>
                  </TableCell>
                  <TableCell>
                    {admin.created_at ? new Date(admin.created_at).toLocaleDateString() : "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setEditAdmin({...admin})}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => setDeleteAdmin(admin)} 
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add City Admin</DialogTitle>
            <DialogDescription>
              Assign someone to manage a specific city
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input 
                value={newAdmin.name} 
                onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} 
                placeholder="Full Name" 
              />
            </div>
            <div>
              <Label>Phone Number *</Label>
              <Input 
                value={newAdmin.phone} 
                onChange={(e) => setNewAdmin({ ...newAdmin, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} 
                placeholder="10-digit phone number"
                maxLength={10}
              />
            </div>
            <div>
              <Label>Assign to City *</Label>
              <Select 
                value={newAdmin.city_id} 
                onValueChange={(v) => setNewAdmin({ ...newAdmin, city_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a city" />
                </SelectTrigger>
                <SelectContent>
                  {cities.map((city) => (
                    <SelectItem key={city.id} value={city.id}>
                      {city.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate}>Create City Admin</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editAdmin} onOpenChange={(open) => !open && setEditAdmin(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit City Admin</DialogTitle>
            <DialogDescription>
              Update city admin details or reassign to a different city
            </DialogDescription>
          </DialogHeader>
          {editAdmin && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Name</Label>
                <Input 
                  value={editAdmin.name} 
                  onChange={(e) => setEditAdmin({ ...editAdmin, name: e.target.value })} 
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input 
                  value={editAdmin.phone || ""} 
                  onChange={(e) => setEditAdmin({ ...editAdmin, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} 
                  maxLength={10}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={editAdmin.email || ""} 
                  onChange={(e) => setEditAdmin({ ...editAdmin, email: e.target.value })} 
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <Label>Assigned City</Label>
                <Select 
                  value={editAdmin.assigned_city_id} 
                  onValueChange={(v) => setEditAdmin({ ...editAdmin, assigned_city_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {cities.map((city) => (
                      <SelectItem key={city.id} value={city.id}>
                        {city.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <Label className="text-sm font-medium">Status</Label>
                  <p className="text-xs text-muted-foreground">
                    {editAdmin.is_active !== false ? 'City admin can access the dashboard' : 'City admin access is disabled'}
                  </p>
                </div>
                <Switch 
                  checked={editAdmin.is_active !== false} 
                  onCheckedChange={(checked) => setEditAdmin({ ...editAdmin, is_active: checked })} 
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAdmin(null)}>Cancel</Button>
            <Button onClick={handleUpdate}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteAdmin} onOpenChange={() => setDeleteAdmin(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete City Admin</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteAdmin?.name}</strong>? 
              They will lose access to manage {deleteAdmin?.assigned_city_name || 'their city'}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CityAdminsManagement;
